import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  userService,
  ServiceOnMount,
  profileService,
  mediaService, mapRole,
} from "../services";
import { Skeleton } from "../components/ui/skeleton";
import { Input } from "../components/ui/input";
import useUserStore from "../stores/userStore";
import useUserStore2 from "../stores/userStore2";
import ShortenedText from "../components/ShortenedText";
import { FaDownload, FaEye, FaEyeSlash } from "react-icons/fa";
import { LuPencil } from "react-icons/lu";
import { GoCheckCircleFill, GoPerson, GoXCircleFill } from "react-icons/go";
import Modal from "../components/Modal";
import { QRCodeCanvas } from "qrcode.react";
import { Star, UserIcon } from "lucide-react";
import { FiEdit2 } from "react-icons/fi";
import { toast } from "react-toastify";
import { DataTable } from "../components/DataTable";

const columns = [
  {
    accessorKey: "id",
    header: "Profile ID",
    cell: ({ row }) => <ShortenedText text={row.getValue("id")} />,
  },
  {
    accessorKey: "name",
    header: "Profile Name",
    cell: ({ row }) => <span>{row.getValue("name")}</span>,
  },
  {
    accessorKey: "profStatus",
    header: "Status",
    cell: ({ row }) => (
      <div
        className={`text-sm font-semibold py-1 px-3 rounded-3xl inline-block ${
          row.getValue("profStatus") === "A"
            ? "text-[#16A34A] bg-[#DCFCE7]"
            : row.getValue("profStatus") === "S"
            ? "text-[#EF4444] bg-[#FEE2E2]"
            : "text-[#EF4444] bg-[#FEE2E2]"
        }`}
      >
        {row.getValue("profStatus") === "A"
          ? "Active"
          : row.getValue("profStatus") === "S"
          ? "Suspended"
          : "Deactivated"}
      </div>
    ),
  },
  {
    accessorKey: "memberships",
    header: "Total Memberships",
    cell: ({ row }) => <span>{row.getValue("memberships")?.length || 0}</span>,
  },
  {
    accessorKey: "dateCreated",
    header: "Date Created",
    cell: ({ row }) => (
      <span>
        {
          // new Date(row.getValue("dateCreated")).toLocaleDateString("en-GB", {
          //   day: "2-digit",
          //   month: "2-digit",
          //   year: "numeric",
          //   hour: "2-digit",
          //   minute: "2-digit",
          // })
          row.getValue("dateCreated")
        }
      </span>
    ),
  },
  {
    accessorKey: "status",
    header: "Primary",
    cell: ({ row }) => (
      <div
        className={`text-sm font-semibold py-0.5 px-2 rounded-3xl inline-flex items-center gap-1 ${
          row.getValue("status") === "Primary"
            ? "text-green-600 bg-yellow-400"
            : row.getValue("profStatus") === ""
            ? "text-[] bg-[]"
            : "text-[] bg-[]"
        }`}
      >
        {row.getValue("status") === "Primary" && (
          <GoCheckCircleFill className="h-3 w-3" />
        )}
        {row.getValue("status") === "Primary"
          ? "Primary"
          : row.getValue("profStatus") === ""
          ? ""
          : ""}
      </div>
    ),
  },
];

const EditOwnAccount = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [isQrModalOpen, setIsQrModalOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [formData, setFormData] = useState({
    userId: "",
    firstName: "",
    lastName: "",
    nric: "",
    phone: "",
    email: "",
    status: "",
    role: "",
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // State for password modal
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [passwordFormData, setPasswordFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // States for toggling password visibility
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [reload, setReload] = useState(false);
  const updateUserStore = useUserStore((state) => state.updateUserStore);
  const { user, updateUser } = useUserStore2();
  const profilePicture = user?.profilePicture;

  const handleSave = async () => {
    try {
      setLoading(true); // Start loading

      const updatedUserData = await userService.editUserDetails({
        EntityId: formData.userId,
        FirstName: formData.firstName,
        LastName: formData.lastName,
        NRIC: formData.nric,
        Phone: formData.phone,
        Email: formData.email,
        Role: formData.role,
      });

      console.log("Full API Response:", updatedUserData);

      if (
        !updatedUserData ||
        !Array.isArray(updatedUserData.Data) ||
        updatedUserData.Data.length === 0
      ) {
        console.error("User data update failed. No valid data returned.");
        toast.error("❌ Failed to update user details", {
          autoClose: 2000,
        });
        return;
      }

      const updatedUser = updatedUserData.Data[0];
      console.log(`updatedUser:`, updatedUser);

      if (!updatedUser || !updatedUser?.first_name || !updatedUser?.last_name) {
        console.error("Missing required user fields:", updatedUser);
        toast.error("❌ Failed to update user details", {
          autoClose: 2000,
        });
        return;
      }

      updateUserStore({
        // userId: updatedUser?._id, // No need to update userId
        firstName: updatedUser?.first_name,
        lastName: updatedUser?.last_name,
        nric: updatedUser?.nric,
        phone: updatedUser?.phone,
        // userRole: updatedUser?.role, // role not returned from API response
      });

      const userData = {
        // id: user?.id,
        firstName: updatedUser?.first_name,
        lastName: updatedUser?.last_name,
        name: updatedUser?.first_name + " " + updatedUser?.last_name,
        nric: updatedUser?.nric,
        phone: updatedUser?.phone,
        role: formData.role,
      }
      console.log(`userData:`, userData);
      console.log(`formData.role:`, formData.role);

      updateUser(userData);

      console.log("Updated Zustand store:", useUserStore.getState());
      navigate(`/edit-account/${userId}`);
    } catch (error) {
      console.error("Error updating user details:", error);
      toast.error("❌ Failed to update user details", {
        autoClose: 2000,
      });
    } finally {
      setLoading(false); // Stop loading
    }
  };

  const roleMapping = {
    A: "Super Admin",
    CA: "Community Admin",
    GL: "Group Leader",
    C: "Collector",
    POCA: "POC Attendant",
    U: "User",
  };

  const dataMap = (item) => ({
    userId: item._id,
    username: item.name,
    status: item.status,
    firstName: item.first_name,
    lastName: item.last_name,
    nric: item.nric,
    phone: item.phone,
    email: item.email,
    role: roleMapping[item.role] || "Unknown Role",
    createDate: item.create_date
      ? new Date(item.create_date).toLocaleDateString()
      : "Not Available",
    lastLogin: item.last_login
      ? new Date(item.last_login).toLocaleString()
      : "Not Available",
  });

  const getUserDetails = ServiceOnMount(
    () => userService.getUserDetails(userId),
    dataMap
  );

  // CC: This is temporarily still needed as userStore does not have nric, phone and email
  // Lutfi: We should really stop using userStore and use userStore2 instead  :p
  useEffect(() => {
    if (getUserDetails.data?.[0]) {
      console.log(
        `EditAccount useEffect getUserDetails.data?.[0]:`,
        getUserDetails.data?.[0]
      );
      setFormData(getUserDetails.data[0]);
    }
  }, [getUserDetails.data]);

  // CC: Use the userStore state to hydrate the form
  useEffect(() => {
    if (user?.firstName) {
      console.log(`EditAccount useEffect firstName:`, user?.firstName);
      setFormData({
        firstName: user?.firstName,
        lastName: user?.lastName,
        role: user?.role,
        nric: user?.nric,
        phone: user?.phone,
        email: user?.email,
      });
    }
  }, [user?.firstName]);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [profileName, setProfileName] = useState("");
  const [isPrimary, setIsPrimary] = useState(false);

  const handleEditDetails = (profile) => {
    setSelectedProfile(profile);
    setProfileName(profile.name); // Prefill with existing name
    setIsPrimary(false); // Default to false (optional)
    setIsEditModalOpen(true);
  };

  const handleSaveProfile = async () => {
    try {
      setLoading(true);
      await profileService.editProfile({
        EntityId: user.id, // Ensure you have user ID
        ProfileId: selectedProfile.id,
        Name: profileName,
        IsPrimary: isPrimary || undefined, // Send only if true
      });

      // Update the profile list in Zustand store
      useUserStore2.setState((state) => {
        if (state.user) {
          state.user.profiles = state.user.profiles.map((profile) =>
            profile.id === selectedProfile.id
              ? { ...profile, name: profileName, isPrimary: isPrimary }
              : profile
          );
        }
      });

      setIsEditModalOpen(false);
      console.log("Profile updated successfully");
    } catch (error) {
      console.error("Error updating profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleActivateProfile = async (profile) => {
    setSelectedProfile(profile);
    setShowActivateModal(true);
  };

  const handleDeactivateProfile = async (profile) => {
    setSelectedProfile(profile);
    setShowDeactivateModal(true);
  };

  const confirmActivateProfile = async () => {
    if (!selectedProfile) return;

    try {
      setLoading(true);
      await profileService.editProfile({
        EntityId: user.id,
        ProfileId: selectedProfile.id,
        Status: "A",
      });

      // Update Zustand store
      useUserStore2.setState((state) => {
        if (state.user) {
          state.user.profiles = state.user.profiles.map((p) =>
            p.id === selectedProfile.id ? { ...p, profStatus: "A" } : p
          );
        }
      });

      // Refresh the profile list
      setReload(!reload);
      setShowActivateModal(false);
      toast.success("🎉 Profile activated successfully!", {
        autoClose: 2000,
      });
    } catch (error) {
      console.error("Error activating profile:", error);
      toast.error("❌ Failed to activate profile", {
        autoClose: 2000,
      });
    } finally {
      setLoading(false);
    }
  };

  const confirmDeactivateProfile = async () => {
    if (!selectedProfile) return;

    try {
      setLoading(true);
      await profileService.editProfile({
        EntityId: user.id,
        ProfileId: selectedProfile.id,
        Status: "D",
      });

      // Update Zustand store
      useUserStore2.setState((state) => {
        if (state.user) {
          state.user.profiles = state.user.profiles.map((p) =>
            p.id === selectedProfile.id ? { ...p, profStatus: "D" } : p
          );
        }
      });

      // Refresh the profile list
      setReload(!reload);
      setShowDeactivateModal(false);
      toast.success("🎉 Profile deactivated successfully!", {
        autoClose: 2000,
      });
    } catch (error) {
      console.error("Error deactivating profile:", error);
      toast.error("❌ Failed to deactivate profile", {
        autoClose: 2000,
      });
    } finally {
      setLoading(false);
    }
  };

  const actions = (profile) => {
    const baseActions = [
      {
        label: "View Details",
        icon: <FaEye />,
        onClick: () => navigate(`/edit-profile/${profile.id}`),
        variant: "dropPlain",
      },
      {
        label: "Edit Details",
        icon: <LuPencil />,
        onClick: () => handleEditDetails(profile),
        variant: "dropPlain",
      },
      {
        label: "Set as Primary",
        icon: <Star />,
        onClick: async () => {
          try {
            await profileService.editProfile({
              EntityId: user.id,
              ProfileId: profile.id,
              Name: profile.name,
              IsPrimary: true,
            });

            useUserStore2.setState((state) => {
              if (state.user) {
                state.user.primaryProfile = {
                  id: profile.id,
                  name: profile.name,
                  type: profile.type,
                  status: profile.status,
                  createDate: profile.createDate,
                  lastUpdate: profile.lastUpdate,
                };
              }
            });

            console.log(`Profile ${profile.name} set as primary successfully`);
          } catch (error) {
            console.error("Error setting profile as primary:", error);
          }
        },
        variant: "dropPlain",
      },
      {
        label: "Select Profile",
        icon: <GoPerson />,
        onClick: () => {
          useUserStore.setState({
            currentProfile: {
              profileId: profile.id,
              name: profile.name,
            },
          });
          console.log(`Switched to profile: ${profile.name}`);
        },
        variant: "dropPlain",
      },
    ];

    // Add activate/deactivate action based on status
    if (profile.profStatus === "D") {
      baseActions.push({
        label: "Activate",
        icon: <GoCheckCircleFill />,
        onClick: () => handleActivateProfile(profile),
        variant: "dropGreen",
      });
    } else if (profile.profStatus === "A") {
      baseActions.push({
        label: "Deactivate",
        icon: <GoXCircleFill />,
        onClick: () => handleDeactivateProfile(profile),
        variant: "dropRed",
      });
    }

    return baseActions;
  };

  const handlePasswordInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleChangePassword = async () => {
    const { currentPassword, newPassword, confirmPassword } = passwordFormData;

    if (newPassword !== confirmPassword) {
      setError("New password and confirm password must match.");
      return;
    }

    setError("");

    try {
      setLoading(true);
      await userService.resetPassword({
        EntityId: userId,
        OldPassword: currentPassword,
        NewPassword: newPassword,
      });

      toast.success("🎉 Password changed successfully!", {
        autoClose: 2000,
      });

      // Reset form and close modal
      setPasswordFormData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setIsPasswordModalOpen(false);
    } catch (error) {
      setError("Failed to reset password. Please try again.");
      console.error("Password reset error:", error);
    } finally {
      setLoading(false);
    }
  };

  let tableData = [];

  const setTableData = () => {
    tableData =
      user?.profiles?.map((profile) => ({
        id: profile.id,
        name: profile.name,
        status: profile.status,
        profStatus: profile.profStatus,
        memberships: profile.memberships || [],
        dateCreated: profile.dateCreated,
        // Include original profile for action handlers
        original: profile,
      })) || [];
    console.log("setTableData: ", tableData);
  }
  setTableData();

  useEffect(() => {
    setTableData();
  }, [user?.profiles]);


  return (
    <div className="flex flex-col min-h-screen">
      <div className="p-8 flex flex-col">
        <div className="bg-white shadow p-6 rounded-lg">
          {/* Header with buttons */}
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-2xl font-semibold text-gray-900">
              Edit Account
            </h1>
            <div className="flex gap-3">
              {/* <Button
                variant="hollowGreen"
                onClick={() => navigate(`/view-account/${userId}`)}
              >
                Cancel
              </Button> */}
              <Button onClick={handleSave} disabled={loading}>
                {loading ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>

          {/* Three-column layout */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Column 1: Profile Picture and Date Created */}
            <div className="flex flex-col items-center space-y-6">
              {/* Profile Picture */}
              <div className="relative">
                <div className="h-32 w-32 rounded-full bg-gray-200 flex items-center justify-center">
                  {profilePicture ? (
                    <img
                      src={profilePicture}
                      alt="User Profile"
                      className="h-full w-full rounded-full object-cover"
                      onError={(e) => {
                        e.target.src = "";
                        e.target.className = "hidden";
                      }}
                    />
                  ) : (
                    <UserIcon className="h-8 w-8 text-gray-500" />
                  )}
                </div>
                <button
                  className="absolute bottom-2 right-2 bg-white p-2 rounded-full shadow-md hover:bg-gray-100 transition-colors"
                  onClick={() =>
                    document.getElementById("profile-picture-upload").click()
                  }
                  title="Change profile picture"
                >
                  <FiEdit2 className="text-gray-600" size={16} />
                </button>
                <input
                  id="profile-picture-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (file) {
                      try {
                        setLoading(true);
                        const response = await mediaService.mobileUploadMedia(
                          file,
                          user.id,
                          { type: "profile_picture" }
                        );

                        if (response?.Data?.media_url) {
                          updateUser({
                            profilePicture: response.Data.media_url,
                          });
                        }

                        toast.success(
                          "🎉 Profile picture updated successfully!",
                          {
                            autoClose: 2000,
                          }
                        );
                      } catch (error) {
                        console.error(
                          "Error uploading profile picture:",
                          error
                        );
                        toast.error("❌ Failed to update profile picture", {
                          autoClose: 2000,
                        });
                      } finally {
                        setLoading(false);
                      }
                    }
                  }}
                />
              </div>

              {/* Status Badge */}
              <div className="w-full flex justify-center">
                <div
                  className={`text-sm py-1 px-3 rounded-3xl ${
                    user?.status === "A"
                      ? "text-[#16A34A] bg-[#DCFCE7]"
                      : user?.status === "S"
                      ? "text-[#EF4444] bg-[#FEE2E2]"
                      : "text-[#CA8A04] bg-[#FEF9C3]"
                  }`}
                >
                  {user?.status === "A"
                    ? "Active"
                    : user?.status === "S"
                    ? "Suspended"
                    : "Deactivated"}
                </div>
              </div>

              {/* Date Created */}
              <div className="w-full flex flex-col items-center space-y-1">
                <Label className="">Date Created</Label>
                <div className="text-sm text-gray-700 px-3 py-1.5 bg-gray-50 rounded text-center">
                  {user?.createDate || <Skeleton className="w-24 h-4" />}
                </div>
              </div>

              {/* Last Login */}
              {/* <div className="w-full flex flex-col items-center space-y-2">
                <Label>Last Login</Label>
                <div className="text-sm text-gray-700 p-2 bg-gray-50 rounded text-center">
                  {user?.lastLogin || <Skeleton className="w-32 h-4" />}
                </div>
              </div> */}
            </div>

            {/* Column 2: First set of form fields */}
            <div className="space-y-4">
              <div>
                <Label>First Name</Label>
                <Input
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                />
              </div>
              <div>
                <Label>Last Name</Label>
                <Input
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                />
              </div>
              <div>
                <Label>Designation</Label>
                <Input
                  name="designation"
                  value={formData.role}
                  onChange={handleInputChange}
                  disabled
                  className="bg-gray-100"
                />
              </div>
              {/* Change Password Button - Modified for right alignment */}
              <div className="pt-4 mt-4">
                <div className="flex items-center justify-between">
                  {" "}
                  {/* Changed from gap-4 to justify-between */}
                  <div>
                    <div className="font-semibold">Change Password</div>
                    <div className="text-sm text-gray-500">
                      Set a new secure password
                    </div>
                  </div>
                  <Button
                    onClick={() => setIsPasswordModalOpen(true)}
                    className="whitespace-nowrap ml-auto"
                  >
                    Change Password
                  </Button>
                </div>
              </div>
            </div>

            {/* Column 3: Second set of form fields */}
            <div className="space-y-4">
              <div>
                <Label>NRIC</Label>
                <Input
                  name="nric"
                  value={formData.nric}
                  onChange={handleInputChange}
                />
              </div>
              <div>
                <Label>Mobile Number</Label>
                <Input
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled
                  className="bg-gray-100"
                />
              </div>
            </div>
          </div>
          <div className="border p-4 mt-4 w-full">
            <div className="text-base sm:text-lg font-semibold text-[#111827]">
              Profiles list
            </div>
            <DataTable columns={columns} data={tableData} actions={actions} />
          </div>
        </div>
      </div>

      {/* Password Change Modal */}
      {isPasswordModalOpen && (
        <Modal
          isOpen={isPasswordModalOpen}
          onClose={() => setIsPasswordModalOpen(false)}
        >
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">
              Change Password
            </h2>

            <div>
              <Label>Current Password</Label>
              <div className="relative">
                <Input
                  name="currentPassword"
                  type={showCurrentPassword ? "text" : "password"}
                  value={passwordFormData.currentPassword}
                  onChange={handlePasswordInputChange}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-3 flex items-center"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? <FaEye /> : <FaEyeSlash />}
                </button>
              </div>
            </div>

            <div>
              <Label>New Password</Label>
              <div className="relative">
                <Input
                  name="newPassword"
                  type={showNewPassword ? "text" : "password"}
                  value={passwordFormData.newPassword}
                  onChange={handlePasswordInputChange}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-3 flex items-center"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <FaEye /> : <FaEyeSlash />}
                </button>
              </div>
            </div>

            <div>
              <Label>Confirm New Password</Label>
              <div className="relative">
                <Input
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={passwordFormData.confirmPassword}
                  onChange={handlePasswordInputChange}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <FaEye /> : <FaEyeSlash />}
                </button>
              </div>
            </div>

            {error && <p className="text-red-500 text-sm">{error}</p>}

            <div className="flex justify-end gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setIsPasswordModalOpen(false);
                  setError("");
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleChangePassword} disabled={loading}>
                {loading ? "Changing..." : "Change Password"}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit profile modal */}
      {isEditModalOpen && (
        <Modal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
        >
          {/* Header */}
          <div className="flex justify-between items-center border-b pb-3">
            <h2 className="text-lg font-semibold text-gray-900">
              Edit Profile
            </h2>
          </div>

          {/* Input for Profile Name */}
          <div className="mt-4 text-left">
            <Label className="block mb-1">Profile Name</Label>
            <Input
              type="text"
              value={profileName}
              onChange={(e) => setProfileName(e.target.value)}
              className="text-left"
            />
          </div>

          {/* Radio Button for Primary */}
          <div className="mt-4 flex items-center">
            <input
              type="radio"
              checked={isPrimary}
              onChange={() => setIsPrimary(!isPrimary)}
              className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
            />
            <label className="ml-2 text-sm text-gray-700">Set as Primary</label>
          </div>

          {/* Buttons */}
          <div className="flex justify-end mt-6 gap-3">
            <Button
              variant={"secondary"}
              onClick={() => setIsEditModalOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveProfile} disabled={loading}>
              {loading ? "Saving..." : "Save"}
            </Button>
          </div>
        </Modal>
      )}

      <Modal
        isOpen={showActivateModal}
        onClose={() => setShowActivateModal(false)}
        title="Activate Profile"
        description={`Are you sure you want to activate ${selectedProfile?.name}?`}
        icon={<GoCheckCircleFill className="text-green-500" />}
        iconColor="green"
        actions={
          <div className="flex justify-between w-full">
            <Button
              variant={"secondary"}
              onClick={() => setShowActivateModal(false)}
            >
              Cancel
            </Button>
            <Button onClick={confirmActivateProfile} disabled={loading}>
              {loading ? "Activating..." : "Activate"}
            </Button>
          </div>
        }
      />

      <Modal
        isOpen={showDeactivateModal}
        onClose={() => setShowDeactivateModal(false)}
        title="Deactivate Profile"
        description={`Are you sure you want to Deactivate ${selectedProfile?.name}?`}
        icon={<GoXCircleFill />}
        iconColor="red"
        actions={
          <div className="flex justify-between w-full">
            <Button
              variant={"secondary"}
              onClick={() => setShowDeactivateModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant={"destructive"}
              onClick={confirmDeactivateProfile}
              disabled={loading}
            >
              {loading ? "Deactivating..." : "Deactivate"}
            </Button>
          </div>
        }
      />

      {/* QR Code Modal */}
      {selectedProfile && (
        <Modal isOpen={isQrModalOpen} onClose={() => setIsQrModalOpen(false)}>
          {/* Header */}
          <div className="flex justify-between items-center border-b pb-3">
            <h2 className="text-lg font-semibold text-gray-900">
              Profile QR Code
            </h2>
          </div>

          {/* Profile Information */}
          <div className="space-y-3 text-sm border-t pt-4">
            {/* Profile ID */}
            <div className="flex text-left bg-gray-50 px-3 py-2 rounded">
              <span className="text-gray-500 w-32">Profile ID</span>
              <span className="text-gray-900 font-medium">
                {selectedProfile.profileId}
              </span>
            </div>
          </div>

          {/* Profile Name */}
          <div className="flex text-left bg-gray-50 px-3 py-2 rounded">
            <span className="text-gray-500 w-32">Profile Name</span>
            <span className="text-gray-900 font-medium">
              {selectedProfile.name}
            </span>
          </div>

          <div className="flex flex-col sm:flex-row text-left bg-gray-50 px-3 py-2 rounded">
            <span className="text-gray-500 w-32 mb-2 sm:mb-0 shrink-0">
              Profile QR
            </span>
            <div className="text-gray-900 font-medium">
              <div className="flex justify-center sm:justify-start">
                <QRCodeCanvas
                  value={`${user.id}/${selectedProfile.id}`}
                  size={200}
                />
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex justify-between gap-3 mt-6">
            <Button
              variant="outline"
              className="px-4 py-2 border rounded-md text-gray-600 hover:bg-gray-100"
              onClick={() => setIsQrModalOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={() => setIsQrModalOpen(false)}>OK</Button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default EditOwnAccount;
